import type { AccountDeletionPreview } from '@platewell/schema';
import { useEffect, useState, type FormEvent } from 'react';
import { Dialog } from '../../components/dialog.js';
import { accountDeletionPreview, deleteAccount } from '../../lib/api.js';

/**
 * The way out, at the bottom of the Account tab.
 *
 * Kept off the page until asked for: the dialog fetches what deleting would do, so the
 * consequences are read from the server's own plan rather than guessed at here.
 */
export function DeleteAccountSection() {
  const [open, setOpen] = useState(false);

  return (
    <section className="section-group">
      <h2 className="section-title">Delete account</h2>
      <p className="hint">
        Permanently delete your account and sign out on every device. This cannot be undone.
      </p>
      <div className="row">
        <button className="btn danger" type="button" onClick={() => setOpen(true)}>
          Delete account…
        </button>
      </div>
      {open && <DeleteAccountDialog onClose={() => setOpen(false)} />}
    </section>
  );
}

function DeleteAccountDialog({ onClose }: { onClose: () => void }) {
  const [preview, setPreview] = useState<AccountDeletionPreview | null>(null);
  const [failed, setFailed] = useState(false);
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    accountDeletionPreview()
      .then((value) => {
        if (!cancelled) setPreview(value);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (preview == null || secret === '' || busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteAccount(
        preview.requiresPassword ? { password: secret } : { confirmEmail: secret },
      );
      // A full load rather than a route change: every session is gone, and the app shell
      // holding this one's households and sync sockets should go with it.
      window.location.assign('/');
    } catch (cause) {
      // The field is left filled: the usual failure is a typo, and retyping is a punishment.
      setError(cause instanceof Error ? cause.message : 'Could not delete your account');
      setBusy(false);
    }
  }

  return (
    <Dialog onClose={busy ? () => undefined : onClose} aria-labelledby="delete-account-title">
      <h2 id="delete-account-title">Delete your account?</h2>

      {failed ? (
        <p className="hint">You need to be online to delete your account.</p>
      ) : preview == null ? (
        <p className="hint">Loading…</p>
      ) : (
        <form onSubmit={(event) => void submit(event)}>
          <Consequences preview={preview} />

          <div className="field" style={{ marginTop: 16 }}>
            {preview.requiresPassword ? (
              <>
                <label className="label" htmlFor="delete-account-password">
                  Enter your password to confirm
                </label>
                <input
                  id="delete-account-password"
                  className="input"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={secret}
                  onChange={(event) => setSecret(event.target.value)}
                />
              </>
            ) : (
              <>
                <label className="label" htmlFor="delete-account-email">
                  Type your email address to confirm
                </label>
                <input
                  id="delete-account-email"
                  className="input"
                  type="email"
                  required
                  autoComplete="off"
                  value={secret}
                  onChange={(event) => setSecret(event.target.value)}
                />
              </>
            )}
          </div>

          {error != null && <p className="error-text">{error}</p>}

          <div className="row" style={{ marginTop: 18 }}>
            <span className="spacer" />
            <button type="button" className="btn ghost" disabled={busy} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn danger" disabled={busy || secret === ''}>
              {busy ? 'Deleting…' : 'Delete my account'}
            </button>
          </div>
        </form>
      )}

      {preview == null && (
        <div className="row" style={{ marginTop: 18 }}>
          <span className="spacer" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      )}
    </Dialog>
  );
}

function Consequences({ preview }: { preview: AccountDeletionPreview }) {
  return (
    <>
      <ul className="hint" style={{ paddingLeft: 20, margin: '8px 0 0' }}>
        {preview.deletedHouseholds.map((household) => (
          <li key={household.id}>
            <strong>{household.name}</strong> and every recipe in it will be permanently deleted.
          </li>
        ))}
        {preview.transferredHouseholds.map((household) => (
          <li key={household.id}>
            {household.newOwnerName || 'Another member'} becomes the owner of{' '}
            <strong>{household.name}</strong>. Its recipes stay with them.
          </li>
        ))}
        {preview.leftHouseholds.map((household) => (
          <li key={household.id}>
            You leave <strong>{household.name}</strong>. Its recipes stay with the other members.
          </li>
        ))}
        <li>You will be signed out on every device.</li>
      </ul>

      {preview.subscriptions.map((subscription) => (
        <p className="error-text" key={`${subscription.householdId}-${subscription.store}`}>
          Your subscription for {subscription.householdName} keeps renewing until you cancel it.
          Deleting your account does not cancel it.{' '}
          {subscription.manageUrl != null ? (
            <a href={subscription.manageUrl} target="_blank" rel="noreferrer">
              Manage subscription
            </a>
          ) : (
            <>Cancel it first under Settings, Subscription.</>
          )}
        </p>
      ))}
    </>
  );
}
